import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, deleteDoc } from "firebase/firestore";
import { PlusCircle, FileText, Edit, X, Trash2, Download, Search, AlertTriangle, ArrowUpDown } from 'lucide-react';
import SheetModal from '../SheetModal';

// --- Helper function to generate file name ---
const generateSheetFileName = (sheet, jobData) => {
    if (!jobData || !sheet || !sheet.block || !sheet.sheetNumber) return sheet.hri || 'N/A';

    const jobNumber = jobData.id.replace('J-', '');
    const blockObject = jobData.blocks?.find(b => b.name === sheet.block);
    const blockCode = blockObject?.code || sheet.block.replace(/\s+/g, '_').toUpperCase();
    const baseName = `${jobNumber}-${blockCode}_${String(sheet.sheetNumber).padStart(3, '0')}`;
    
    if (!sheet.isPrinting) {
        return `${baseName}N`;
    } else {
        if (sheet.printFaces == 1) {
            return `${baseName}P`;
        } else if (sheet.printFaces == 2) {
            // For the table, we just show the base name for 2-sided sheets, 
            // as it refers to both A and B files.
            return `${baseName}A/B`;
        }
    }
    return sheet.hri; // Fallback
};


// --- Componentes de UI (Modales) ---

const ConfirmDeleteModal = ({ isOpen, onClose, onConfirm, sheetHri }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
                <div className="p-4 border-b flex items-center gap-3">
                    <AlertTriangle className="text-red-500" size={24} />
                    <h2 className="text-lg font-bold text-gray-800">Confirmar Eliminación</h2>
                </div>
                <div className="p-6">
                    <p className="text-sm text-gray-700">
                        ¿Estás seguro de que quieres eliminar la plancha <strong className="font-mono bg-gray-100 px-1 rounded">{sheetHri}</strong>?
                        <br /><br />
                        Esta acción no se puede deshacer y borrará permanentemente la plancha y sus archivos asociados.
                    </p>
                </div>
                <div className="flex justify-end p-4 bg-gray-50 space-x-3 rounded-b-lg">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancelar</button>
                    <button onClick={onConfirm} className="px-4 py-2 bg-red-600 text-white font-bold rounded-md hover:bg-red-700">Sí, Eliminar</button>
                </div>
            </div>
        </div>
    );
};

const ImageViewerModal = ({ imageUrl, onClose }) => {
    if (!imageUrl) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-center items-center" onClick={onClose}>
            <div className="relative p-4">
                <button onClick={onClose} className="absolute -top-4 -right-4 bg-white rounded-full p-1 shadow-lg"><X size={24} className="text-gray-800" /></button>
                <img src={imageUrl} alt="Vista ampliada" className="max-h-[90vh] max-w-[90vw] object-contain" />
            </div>
        </div>
    );
};
const LogModal = ({ isOpen, onClose, history }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-xl font-bold text-gray-800">Historial de Cambios</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={24} /></button>
                </div>
                <div className="p-6 overflow-y-auto">
                    <ul className="space-y-4">
                        {history && history.length > 0 ? (
                            [...history].reverse().map((entry, index) => (
                                <li key={index} className="border-b pb-2 list-none">
                                    <p className="font-semibold text-gray-800">{entry.action}</p>
                                    <p className="text-sm text-gray-600">Por: {entry.user}</p>
                                    <p className="text-xs text-gray-400">Fecha: {entry.timestamp?.toDate ? entry.timestamp.toDate().toLocaleString() : 'Fecha no disponible'}</p>
                                </li>
                            ))
                        ) : (
                            <p className="text-gray-500">No hay historial de cambios para esta plancha.</p>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
};

// --- Componente Principal ---
const DesignManagerTab = ({ user, db, storage, jobData, sheets, materialsCatalog }) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [sheetToDelete, setSheetToDelete] = useState(null);
  const [selectedSheetHistory, setSelectedSheetHistory] = useState([]);
  const [editingSheet, setEditingSheet] = useState(null);
  const [viewerImage, setViewerImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAll, setShowAll] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'hri', direction: 'ascending' });

  const getMaterialName = useCallback((materialId) => {
    if (!materialsCatalog || materialsCatalog.length === 0) return 'Cargando...';
    return materialsCatalog.find(m => m.id === materialId)?.name || 'Unknown';
  }, [materialsCatalog]);

  const handleNewSheet = () => { setEditingSheet(null); setIsModalOpen(true); };
  const handleEditSheet = (sheet) => { setEditingSheet(sheet); setIsModalOpen(true); };
  const handleShowLog = (history) => { setSelectedSheetHistory(history || []); setIsLogModalOpen(true); };

  const handleDeleteClick = (sheet) => {
      setSheetToDelete(sheet);
      setIsConfirmDeleteOpen(true);
  };
  const handleConfirmDelete = async () => {
    if (!sheetToDelete) return;
    try {
        await deleteDoc(doc(db, `jobs/${jobData.id}/sheets`, sheetToDelete.id));
    } catch (error) {
        console.error("Error deleting sheet:", error);
        alert("No se pudo eliminar la plancha.");
    } finally {
        setIsConfirmDeleteOpen(false);
        setSheetToDelete(null);
    }
  };

  const handleCloseModal = () => { setIsModalOpen(false); setEditingSheet(null); };
  const getPrintType = (sheet) => {
    if (!sheet.isPrinting) return 'Solo Corte';
    return sheet.printFaces == 2 ? '2 Caras' : '1 Cara';
  };
  
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
        direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  
  const sortedAndFilteredSheets = useMemo(() => {
    if (!sheets) return [];

    const filteredByView = sheets.filter(sheet => {
        if (showAll) return true;
        return ['Hold', 'Devuelto a Diseño'].includes(sheet.status);
    });

    const lowercasedSearch = searchTerm.toLowerCase().trim();
    const filteredBySearch = lowercasedSearch ? filteredByView.filter(sheet => {
        const fileName = generateSheetFileName(sheet, jobData);
        const searchableString = `${fileName} ${getMaterialName(sheet.materialId)} ${getPrintType(sheet)} ${(sheet.pieces || []).map(p => `${p.code} ${p.name}`).join(' ')} ${sheet.status || ''}`;
        return searchableString.toLowerCase().includes(lowercasedSearch);
    }) : filteredByView;

    return [...filteredBySearch].sort((a, b) => {
        let aValue, bValue;
        if (sortConfig.key === 'fileName') {
            aValue = generateSheetFileName(a, jobData);
            bValue = generateSheetFileName(b, jobData);
        } else if (sortConfig.key === 'materialId') {
            aValue = getMaterialName(a.materialId);
            bValue = getMaterialName(b.materialId);
        } else if (sortConfig.key === 'isPrinting') {
            aValue = getPrintType(a);
            bValue = getPrintType(b);
        } else {
            aValue = a[sortConfig.key];
            bValue = b[sortConfig.key];
        }
        
        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
    });
  }, [sheets, searchTerm, sortConfig, getMaterialName, showAll, jobData]);

  const getStatusBadge = (status) => {
      switch (status) {
          case 'Listo para Prepress': return 'bg-green-100 text-green-800';
          case 'Devuelto a Diseño': return 'bg-red-100 text-red-800';
          case 'Corregido por Diseño': return 'bg-purple-100 text-purple-800';
          case 'Hold': return 'bg-yellow-100 text-yellow-800';
          default: return 'bg-gray-100 text-gray-800';
      }
  };
  
  const SortableHeader = ({ label, sortKey, className = '' }) => {
    const isSorted = sortConfig.key === sortKey;
    return (
        <th className={`px-2 py-3 text-center font-medium text-gray-600 uppercase ${className}`}>
            <button onClick={() => requestSort(sortKey)} className="flex items-center justify-center gap-1 w-full">
                {label}
                <ArrowUpDown size={14} className={isSorted ? 'text-gray-800' : 'text-gray-300'} />
            </button>
        </th>
    );
  };

  return (
    <>
      <ImageViewerModal imageUrl={viewerImage} onClose={() => setViewerImage(null)} />
      <LogModal isOpen={isLogModalOpen} onClose={() => setIsLogModalOpen(false)} history={selectedSheetHistory} />
      <ConfirmDeleteModal isOpen={isConfirmDeleteOpen} onClose={() => setIsConfirmDeleteOpen(false)} onConfirm={handleConfirmDelete} sheetHri={sheetToDelete?.hri} />
      
      <div className="flex flex-col h-full">
          <div className="flex justify-between items-center mb-4 gap-4 flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800 flex-shrink-0">
                  {showAll ? 'Todas las Planchas (Diseño)' : 'Mesa de Trabajo de Diseño'}
              </h2>
              <div className="flex items-center gap-3 flex-grow justify-end">
                  <div className="flex items-center space-x-2 bg-gray-100 p-1 rounded-lg">
                      <button onClick={() => setShowAll(false)} className={`px-3 py-1 text-sm font-semibold rounded-md transition-colors ${!showAll ? 'bg-white shadow' : 'text-gray-600'}`}>
                          Tareas Pendientes
                      </button>
                      <button onClick={() => setShowAll(true)} className={`px-3 py-1 text-sm font-semibold rounded-md transition-colors ${showAll ? 'bg-white shadow' : 'text-gray-600'}`}>
                          Ver Todas
                      </button>
                  </div>
                  <div className="relative flex-grow max-w-xs">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                      <input type="text" placeholder="Buscar..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full p-2 pl-10 border rounded-md text-sm" />
                  </div>
                  <button onClick={handleNewSheet} className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-700 flex items-center gap-2"><PlusCircle size={18} /> Nueva Plancha</button>
              </div>
          </div>
          
          <div className="flex-grow overflow-auto border rounded-lg">
              <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0 z-10">
                      <tr>
                          <th className="px-2 py-3 text-center font-medium text-gray-600 uppercase w-[10%]">PREVIEW</th>
                          <SortableHeader label="PLANCHA" sortKey="fileName" className="w-[10%]" />
                          <SortableHeader label="MATERIAL" sortKey="materialId" className="w-[15%]" />
                          <SortableHeader label="IMPRESIÓN" sortKey="isPrinting" className="w-[10%]" />
                          <SortableHeader label="REPETICIONES" sortKey="repetitions" className="w-[10%]" />
                          <th className="px-2 py-3 text-center font-medium text-gray-600 uppercase w-[15%]">PIEZAS</th>
                          <th className="px-2 py-3 text-center font-medium text-gray-600 uppercase w-[10%]">ESTADO</th>
                          <th className="px-2 py-3 text-center font-medium text-gray-600 uppercase w-[10%]">ARCHIVOS</th>
                          <th className="px-2 py-3 text-center font-medium text-gray-600 uppercase w-[10%]">ACCIONES</th>
                      </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                      {!sheets ? (
                        <tr><td colSpan="9" className="text-center py-10 text-gray-500">Cargando planchas...</td></tr>
                      ) : sortedAndFilteredSheets.length > 0 ? sortedAndFilteredSheets.map((sheet) => {
                          // La fila parpadea según el estado: ROJO si devuelta a diseño, AMARILLO si en hold.
                          const blinkClass = sheet.status === 'Devuelto a Diseño'
                              ? 'bg-red-100 animate-pulse'
                              : (sheet.status === 'Hold' ? 'bg-yellow-100 animate-pulse' : 'odd:bg-slate-50');
                          return (
                          <tr key={sheet.id} className={`hover:bg-gray-50 ${blinkClass}`}>
                              <td className="px-2 py-2 text-center align-middle">
                                  <div className="flex justify-center items-center gap-1">
                                      <img 
                                          src={sheet.previewImageFrontUrl || 'https://placehold.co/80x50/eee/ccc?text=F'} 
                                          className="w-12 h-auto object-contain rounded shadow-sm cursor-pointer hover:scale-110 transition-transform" 
                                          alt="Preview Frontal" 
                                          onClick={() => sheet.previewImageFrontUrl && setViewerImage(sheet.previewImageFrontUrl)}
                                      />
                                      {sheet.isPrinting && sheet.printFaces == 2 && (
                                          <img 
                                              src={sheet.previewImageBackUrl || 'https://placehold.co/80x50/eee/ccc?text=B'} 
                                              className="w-12 h-auto object-contain rounded shadow-sm cursor-pointer hover:scale-110 transition-transform" 
                                              alt="Preview Trasera" 
                                              onClick={() => sheet.previewImageBackUrl && setViewerImage(sheet.previewImageBackUrl)}
                                          />
                                      )}
                                  </div>
                              </td>
                              <td className="px-2 py-2 text-center whitespace-nowrap font-mono">{generateSheetFileName(sheet, jobData)}</td>
                              <td className="px-2 py-2 text-center whitespace-nowrap">{getMaterialName(sheet.materialId)}</td>
                              <td className="px-2 py-2 text-center whitespace-nowrap">{getPrintType(sheet)}</td>
                              <td className="px-2 py-2 text-center whitespace-nowrap font-bold">{sheet.repetitions}</td>
                              <td className="px-2 py-2 text-center align-middle">
                                  {(sheet.pieces || []).map((piece, index) => (
                                      <div key={index} className="text-xs truncate" title={`${piece.code} / ${piece.name}`}>{piece.code} / {piece.name} x <span className="font-bold">{piece.qtyPerSheet}</span></div>
                                  ))}
                              </td>
                              <td className="px-2 py-2 text-center align-middle">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(sheet.status)}`}>
                                      {sheet.status === 'Devuelto a Diseño' && <AlertTriangle size={12} className="mr-1.5" />}
                                      {sheet.status || 'N/A'}
                                  </span>
                              </td>
                              <td className="px-2 py-2 text-center align-middle">
                                  <div className="flex flex-col items-center gap-1.5">
                                    {sheet.designFileFront?.url && ( <a href={sheet.designFileFront.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-indigo-600 hover:underline text-xs group"> <Download size={14} className="opacity-50 group-hover:opacity-100"/> <span className="truncate">{sheet.designFileFront.name}</span></a> )}
                                    {sheet.designFileBack?.url && ( <a href={sheet.designFileBack.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-indigo-600 hover:underline text-xs group"> <Download size={14} className="opacity-50 group-hover:opacity-100"/> <span className="truncate">{sheet.designFileBack.name}</span></a> )}
                                  </div>
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap text-center align-middle">
                                  <div className="flex items-center justify-center gap-3">
                                    <button onClick={() => handleShowLog(sheet.history)} title="Ver Historial" className="text-gray-400 hover:text-gray-700"><FileText size={16} /></button>
                                    <button onClick={() => handleEditSheet(sheet)} title="Editar Plancha" className="text-gray-400 hover:text-blue-600"><Edit size={16} /></button>
                                    <button onClick={() => handleDeleteClick(sheet)} title="Eliminar Plancha" className="text-gray-400 hover:text-red-600"><Trash2 size={16} /></button>
                                  </div>
                              </td>
                          </tr>
                          );
                      }) : (
                        <tr><td colSpan="9" className="text-center py-10 text-gray-500">{showAll ? 'No se han creado planchas para este trabajo.' : 'No hay planchas que requieran acción en Diseño.'}</td></tr>
                      )}
                  </tbody>
              </table>
          </div>
      </div>
      
      {isModalOpen && (
        <SheetModal 
            isOpen={isModalOpen} 
            onClose={handleCloseModal} 
            jobData={jobData} 
            db={db}
            storage={storage}
            user={user}
            initialSheet={editingSheet}
            onSaveSuccess={() => { /* Podríamos añadir un refresco de datos aquí si fuera necesario */}}
            onAddBlockOrComponent={() => { handleCloseModal(); navigate(`/jobs/edit/${jobData.id}?tab=details`); }}
        />
      )}
    </>
  );
};

export default DesignManagerTab;
